import { JSONSchema7 } from 'json-schema';
import { customSchemas, types as commonTypes } from '@sitebuilder/common';
import Manager from './Manager';

const schema: JSONSchema7 = {
    title: 'Manager',
    type: 'object',
    required: ['link', 'linkName'],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        link: {
            type: 'string',
            title: 'Link',
            description: 'URL for the link',
        },
        linkName: {
            type: 'string',
            title: 'Link Name',
            description: 'Link Name',
        },
        isCompany: {
            type: 'boolean',
            description: 'Is it a management comany or individual manager',
            default: false,
            title: 'Is this a management comany',
        },
        isImgLink: {
            type: 'boolean',
            description: 'Is Link',
            default: false,
            title: 'Is Link',
        },
        openInNewTab: {
            type: 'boolean',
            default: true,
            title: 'Open link in new tab',
        },
        altText: {
            type: 'string',
            title: 'Alt Text',
            default: 'image',
        },
        iconName: {
            type: 'string',
            title: 'Icon Name',
            description: 'Icon Name',
        },
        isBioReadMore: {
            type: 'boolean',
            default: false,
            title: 'Implement read more functionality for Bios',
        },
        useAnalytics: {
            type: 'boolean',
            title: 'Use analytics',
            description: 'Enable analytics to be tracked',
        },
    },
    dependencies: {
        isImgLink: {
            oneOf: [
                {
                    properties: {
                        isImgLink: { enum: [true] },
                        imageLink: {
                            type: 'string',
                            title: 'Image Link',
                            description: 'URL for the image',
                        },
                    },
                    required: ['imageLink'],
                },
                {
                    properties: {
                        isImgLink: { enum: [false] },
                        imgFileName: {
                            type: 'string',
                            title: 'File Name',
                            description: 'File name of the image in the repo',
                        },
                        imgFileType: {
                            type: 'string',
                            enum: ['png', 'jpg', 'svg'],
                            title: 'File Type',
                            default: 'png',
                        },
                    },
                    required: ['imgFileName', 'imgFileType'],
                },
            ],
        },
        isCompany: {
            oneOf: [
                {
                    properties: {
                        isCompany: { enum: [true] },
                        companyDetails: {
                            type: 'string',
                            title: 'Company Details',
                            description: 'Details about the Company',
                        },
                        companyName: {
                            type: 'string',
                            title: 'Company Name',
                            description: 'Name of the Company',
                        },
                    },
                    required: ['companyDetails', 'companyName'],
                },
                {
                    properties: {
                        isCompany: { enum: [false] },
                        managerName: {
                            type: 'string',
                            title: 'Manager Name',
                            description: 'Name of the Manager',
                        },
                        managerBio: {
                            type: 'string',
                            title: 'Manager Bio',
                            description: 'Bio of the Manager',
                        },
                    },
                    required: ['managerName'],
                },
            ],
        },
        isBioReadMore: {
            oneOf: [
                {
                    properties: {
                        isBioReadMore: { enum: [true] },
                        readMoreText: {
                            type: 'string',
                            title: 'Read More Text',
                        },
                        readLessText: {
                            type: 'string',
                            title: 'Read Less Text',
                        },
                        numberOfCharacters: {
                            type: 'number',
                            title: 'Number of Characters to show for bios',
                            default: 150,
                        },
                    },
                    required: ['readMoreText', 'readLessText', 'numberOfCharacters'],
                },
            ],
        },
        useAnalytics: {
            oneOf: [
                {
                    properties: {
                        useAnalytics: { enum: [true] },
                        dynamicEvents: {
                            type: 'array',
                            title: 'Add Event with Custom Configuration',
                            items: {
                                type: 'object',
                                properties: {
                                    eventName: {
                                        type: 'string',
                                        title: 'Event Name',
                                        enum: ['Read Bio'],
                                        default: 'Read Bio',
                                    },
                                    customEventName: {
                                        type: 'string',
                                        title: 'Custom Event Name (Optional)',
                                    },
                                    analyticItems: {
                                        type: 'array',
                                        title: 'Page Analytics Items',
                                        items: {
                                            type: 'object',
                                            properties: {
                                                name: {
                                                    type: 'string',
                                                    title: 'Name',
                                                },
                                                value: {
                                                    type: 'string',
                                                    title: 'Value',
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'link',
            'linkName',
            'isCompany',
            'companyDetails',
            'companyName',
            'managerName',
            'managerBio',
            'isImgLink',
            'imageLink',
            'imgFileName',
            'imgFileType',
            'openInNewTab',
            'altText',
            'iconName',
            'isBioReadMore',
            'readMoreText',
            'readLessText',
            'numberOfCharacters',
        ],
        Analytics: ['useAnalytics', 'dynamicEvents'],
    },
];

const uiSchema = {
    'ui:groups': groups,
    ...customSchemas.textSchema.TextSchema.uiSchema,
    ...customSchemas.styleSchema.StyleSchema.uiSchema,
    ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
    ...customSchemas.ComponentInfoSchema.ComponentInfoSchema.uiSchema,
    styles: {
        'ui:widget': 'CSSEditor',
    },
    linkName: {
        'ui:widget': 'ResourcePicker',
    },
    managerName: {
        'ui:widget': 'ResourcePicker',
    },
    managerBio: {
        'ui:widget': 'ResourcePicker',
    },
    companyName: {
        'ui:widget': 'ResourcePicker',
    },
    companyDetails: {
        'ui:widget': 'ResourcePicker',
    },
    readMoreText: {
        'ui:widget': 'ResourcePicker',
    },
    readLessText: {
        'ui:widget': 'ResourcePicker',
    },
    isCompany: {
        'ui:widget': 'CustomCheckbox',
    },
    openInNewTab: {
        'ui:widget': 'CustomCheckbox',
    },
    isImgLink: {
        'ui:widget': 'CustomCheckbox',
    },
    isBioReadMore: {
        'ui:widget': 'CustomCheckbox',
    },
    numberOfCharacters: {
        'ui:widget': 'CustomTextInput',
    },
    imageLink: {
        'ui:widget': 'MediaPicker',
        'ui:options': {
            isImagePicker: true,
        },
    },
    altText: {
        'ui:widget': 'CustomTextInput',
    },
    teamLink: {
        'ui:widget': 'CustomTextInput',
    },
    linkIcon: {
        'ui:widget': 'CustomTextInput',
    },
    iconName: {
        'ui:widget': 'CustomTextInput',
    },
    imgFileName: {
        'ui:widget': 'CustomTextInput',
    },
    link: {
        'ui:widget': 'CustomTextInput',
    },
    imgFileType: {
        'ui:widget': 'CustomSelect',
    },
    useAnalytics: { 'ui:widget': 'CustomCheckbox' },
    dynamicEvents: {
        items: {
            eventName: { 'ui:widget': 'CustomSelect' },
            customEventName: { 'ui:widget': 'CustomTextInput' },
            analyticItems: {
                items: {
                    name: { 'ui:widget': 'CustomTextInput' },
                    value: { 'ui:widget': 'ResourcePicker' },
                },
            },
        },
    },
};

const ManagerWidget: commonTypes.IWidget = {
    schema,
    uiSchema,
    templateView: Manager,
    id: 'Manager',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: () => ({
        linkName: 'About the Portfolio Management',
        link: 'https://www.google.com',
        managerName: 'Joseph F. Hohn',
        iconName: 'fas fa-link',
        altText: 'Joseph F. Hohn',
        companyName: 'MFS Investment Management Canada Limited',
        companyDetails: 'MFS details',
    }),
};

export default ManagerWidget;
